function getRowCount(total) {
	if (total <= 6) {
		return 2;
	}

	if (total <= 12) {
		return 3;
	}

	return 4;
}

const GRID_GAP = 12;
const DESKTOP_BREAKPOINT = 1025;
const DESKTOP_ROW_FILL_TARGET = 0.8;

function seededFloat(seed, salt = 1) {
	const value =
		Math.sin((Number(seed) + 1) * (salt + 1) * 12.9898) * 43758.5453;
	return value - Math.floor(value);
}

function splitIntoRows(items, rowCount = getRowCount(items.length)) {
	const rows = [];
	const baseSize = Math.floor(items.length / rowCount);
	const remainder = items.length % rowCount;
	let startIndex = 0;

	for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
		const rowSize = baseSize + (rowIndex < remainder ? 1 : 0);
		const rowItems = items.slice(startIndex, startIndex + rowSize);

		if (rowItems.length) {
			rows.push(rowItems);
		}

		startIndex += rowSize;
	}

	return rows;
}

function getItemsWidth(items) {
	return items.reduce(
		(sum, item) => sum + item.getBoundingClientRect().width,
		0,
	);
}

function getRowWidth(items) {
	if (!items.length) {
		return 0;
	}

	return getItemsWidth(items) + Math.max(0, items.length - 1) * GRID_GAP;
}

function buildRows(track, items, rowCount) {
	const rows = splitIntoRows(items, rowCount);

	track.classList.remove("is-desktop-balanced");
	track.replaceChildren();

	rows.forEach((row) => {
		const rowNode = document.createElement("div");
		rowNode.className = "sui-topic-cloud-row";

		row.forEach((item) => {
			rowNode.appendChild(item);
		});

		track.appendChild(rowNode);
	});
}

function planDesktopRows(items, maxRowWidth) {
	if (!items.length) {
		return [];
	}

	const totalWidth = getRowWidth(items);
	let rowCount = Math.max(
		getRowCount(items.length),
		Math.ceil(totalWidth / Math.max(maxRowWidth, 1)),
	);
	rowCount = Math.min(rowCount, items.length);

	while (rowCount <= items.length) {
		const rows = splitIntoRows(items, rowCount);
		const widestRow = Math.max(...rows.map((row) => getRowWidth(row)), 0);

		if (widestRow <= maxRowWidth || rowCount === items.length) {
			return rows;
		}

		rowCount += 1;
	}

	return splitIntoRows(items, items.length);
}

function buildDesktopRows(track, items, maxRowWidth) {
	const rows = planDesktopRows(items, maxRowWidth);

	track.classList.add("is-desktop-balanced");
	track.replaceChildren();

	rows
		.filter((row) => row.length)
		.forEach((row) => {
			const rowNode = document.createElement("div");
			rowNode.className = "sui-topic-cloud-row";

			row.forEach((item) => {
				rowNode.appendChild(item);
			});

			track.appendChild(rowNode);
		});
}

function clearDesktopRowFill(items) {
	items.forEach((item) => {
		item.style.removeProperty("--topic-inline-padding-extra");
	});
}

function getWrappedRows(items) {
	const rows = [];
	let currentRow = [];
	let currentTop = null;
	const tolerance = 4;

	items.forEach((item) => {
		const top = item.offsetTop;

		if (currentTop === null || Math.abs(top - currentTop) <= tolerance) {
			currentRow.push(item);
			currentTop = currentTop ?? top;
			return;
		}

		rows.push(currentRow);
		currentRow = [item];
		currentTop = top;
	});

	if (currentRow.length) {
		rows.push(currentRow);
	}

	return rows;
}

function applyDesktopRowFill(track, items) {
	clearDesktopRowFill(items);

	const explicitRows = Array.from(
		track.querySelectorAll(":scope > .sui-topic-cloud-row"),
	)
		.map((rowNode) =>
			Array.from(rowNode.querySelectorAll(".sui-topic-cloud-item")),
		)
		.filter((row) => row.length);
	const rows = explicitRows.length ? explicitRows : getWrappedRows(items);
	const trackWidth = track.clientWidth;

	rows.forEach((row, rowIndex) => {
		if (row.length < 2 || !trackWidth) {
			return;
		}

		const rowWidth =
			row.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) +
			Math.max(0, row.length - 1) * GRID_GAP;
		const remainingWidth = Math.max(0, trackWidth - rowWidth - 2);

		if (remainingWidth < 10) {
			return;
		}

		const desiredSelectionCount = Math.max(
			2,
			Math.ceil(remainingWidth / 56),
			Math.ceil(row.length * 0.55),
		);
		const selectionCount = Math.min(row.length, desiredSelectionCount, 6);
		const selectedItems = [...row]
			.sort(
				(left, right) =>
					seededFloat(left.dataset.seed, rowIndex + 5) -
					seededFloat(right.dataset.seed, rowIndex + 5),
			)
			.slice(0, selectionCount);
		const weights = selectedItems.map(
			(item, selectedIndex) =>
				0.8 +
				seededFloat(item.dataset.seed, rowIndex + selectedIndex + 11) * 0.65,
		);
		const totalWeight = weights.reduce((sum, value) => sum + value, 0);
		let consumedWidth = 0;

		selectedItems.forEach((item, selectedIndex) => {
			const isLast = selectedIndex === selectedItems.length - 1;
			const share = isLast
				? Math.max(0, remainingWidth - consumedWidth)
				: Math.round((remainingWidth * weights[selectedIndex]) / totalWeight);
			const extraPadding = Math.max(0, share / 2);

			item.style.setProperty(
				"--topic-inline-padding-extra",
				`${extraPadding.toFixed(2)}px`,
			);
			consumedWidth += share;
		});
	});
}

export function renderGrid(items, renderItem) {
	const viewport = document.createElement("div");
	viewport.className = "sui-topic-cloud-viewport";

	const track = document.createElement("div");
	track.className = "sui-topic-cloud-track";

	const itemNodes = items.map((item, index) =>
		renderItem(item, index, items.length, `${index}`),
	);

	buildRows(track, itemNodes, getRowCount(itemNodes.length));

	viewport.appendChild(track);

	return viewport;
}

export function initializeGrid(node) {
	const viewport = node.querySelector(".sui-topic-cloud-viewport");
	const track = node.querySelector(".sui-topic-cloud-track");
	const items = Array.from(
		track?.querySelectorAll(".sui-topic-cloud-item") || [],
	);

	if (!viewport || !track || !items.length) {
		return;
	}
	let frameId = 0;

	let currentLayoutKey = "";

	const updatePosition = () => {
		const isDesktop =
			typeof window !== "undefined" && typeof window.matchMedia === "function"
				? window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches
				: viewport.clientWidth >= DESKTOP_BREAKPOINT;
		const desktopTrackWidth = Math.floor(
			viewport.clientWidth * DESKTOP_ROW_FILL_TARGET,
		);
		const nextLayoutKey = isDesktop
			? `desktop:${desktopTrackWidth}`
			: `mobile:${getRowCount(items.length)}`;

		clearDesktopRowFill(items);
		if (nextLayoutKey !== currentLayoutKey) {
			if (isDesktop) {
				track.style.width = `${desktopTrackWidth}px`;
				track.style.maxWidth = `${desktopTrackWidth}px`;
				track.style.minWidth = "0px";
				buildDesktopRows(track, items, desktopTrackWidth);
			} else {
				track.style.width = "";
				clearDesktopRowFill(items);
				track.style.maxWidth = "";
				track.style.minWidth = "";
				buildRows(track, items, getRowCount(items.length));
			}

			currentLayoutKey = nextLayoutKey;
		}
		applyDesktopRowFill(track, items);

		if (isDesktop) {
			viewport.scrollLeft = 0;
		} else {
			const maxOffset = Math.max(0, track.scrollWidth - viewport.clientWidth);
			viewport.scrollLeft = maxOffset / 2;
		}
	};

	updatePosition();

	const scheduleUpdate = () => {
		if (frameId) {
			window.cancelAnimationFrame(frameId);
		}

		frameId = window.requestAnimationFrame(() => {
			frameId = 0;
			updatePosition();
		});
	};

	if (typeof ResizeObserver === "undefined") {
		window.addEventListener("resize", scheduleUpdate);
		return;
	}

	const observer = new ResizeObserver(() => {
		scheduleUpdate();
	});

	observer.observe(viewport);
}
